/**
 * Unified lead address handling.
 * Uses the same logic as Edit Lead (AddEditLeadModal): area, city, street from parsed address, addr.
 * Display everywhere = what Edit Lead shows when you open it.
 */

export type LeadAddressSource = {
  area?: string | null;
  city?: string | null;
  address?: string | null;
  addr?: string | null;
  street?: string | null;
  bexMatchDetails?: {
    municipality?: string | null;
    settlement?: string | null;
    street?: string | null;
    addr?: string | null;
  } | null;
};

/** Same parsing as AddEditLeadModal – street and number from "area, city, street addr" or "street addr". */
function parseAddressComponents(addressString: string): { street: string; number: string } {
  if (!addressString) return { street: "", number: "" };
  let street = addressString.trim();
  let number = "";
  const words = street.split(/\s+/);
  if (words.length >= 4) {
    for (let seqLength = Math.min(5, Math.floor(words.length / 2)); seqLength >= 1; seqLength--) {
      if (words.length < seqLength * 2) continue;
      const lastSequence = words.slice(-seqLength).join(" ");
      const beforeSequence = words.slice(0, -seqLength).join(" ");
      if (beforeSequence.includes(lastSequence)) {
        street = beforeSequence.trim();
        break;
      }
    }
  }
  const numberPatterns = [
    /\s+(br\.?|no\.?|#)\s*(\d+.*)$/i,
    /\s+(\d+\s*\/\s*\d+.*)$/i,
  ];
  for (const pattern of numberPatterns) {
    const match = street.match(pattern);
    if (match) {
      number = (match[match.length - 1] || "").trim();
      street = street.replace(pattern, "").trim();
      break;
    }
  }
  if (!number) {
    const trailingNumberPattern = /^(.+?)\s+(\d+)$/;
    const match = street.match(trailingNumberPattern);
    if (match) {
      const beforeNumber = match[1].trim();
      const potentialNumber = match[2];
      if (
        !beforeNumber.endsWith(".)") &&
        !beforeNumber.match(/\)\s*$/) &&
        !(beforeNumber.split(/\s+/).pop() || "").endsWith(")")
      ) {
        number = potentialNumber;
        street = beforeNumber;
      }
    }
  }
  return { street: street.trim(), number: number.trim() };
}

/**
 * Returns the address string using the same logic as Edit Lead (AddEditLeadModal) when it loads a lead.
 * Use this everywhere (Leads table, duplicate modal, etc.) so the address matches what Edit shows.
 */
export function getLeadDisplayAddress(lead: LeadAddressSource): string {
  if (!lead) return "—";
  const area = lead.area ?? lead.bexMatchDetails?.municipality ?? null;
  const city = lead.city ?? lead.bexMatchDetails?.settlement ?? null;
  const addr = lead.addr ?? lead.bexMatchDetails?.addr ?? null;

  const addressParts = (lead.address ?? "").split(", ");
  const lastPart = addressParts[addressParts.length - 1] ?? "";
  const { street, number } = parseAddressComponents(lastPart);
  const streetName =
    street ||
    (addressParts.length > 1 ? addressParts[addressParts.length - 2] ?? "" : "") ||
    (lead.bexMatchDetails?.street ?? lead.street ?? "");

  const displayNum = addr ?? number ?? null;
  const parts = [area, city, streetName].filter(Boolean);
  if (parts.length === 0) return "—";
  return displayNum ? `${parts.join(", ")}, ${displayNum}` : parts.join(", ");
}

/**
 * Same as getLeadDisplayAddress but with a newline between locality (area, city) and street line.
 * Use in shipping table so address shows as:
 *   OBRENOVAC, ZVEČKA
 *   DRAGA VUKOVIĆA-KORČAGINA, 4
 */
export function getLeadDisplayAddressTwoLines(lead: LeadAddressSource): string {
  if (!lead) return "—";
  const area = lead.area ?? lead.bexMatchDetails?.municipality ?? null;
  const city = lead.city ?? lead.bexMatchDetails?.settlement ?? null;
  const addr = lead.addr ?? lead.bexMatchDetails?.addr ?? null;

  const addressParts = (lead.address ?? "").split(", ");
  const lastPart = addressParts[addressParts.length - 1] ?? "";
  const { street, number } = parseAddressComponents(lastPart);
  const streetName =
    street ||
    (addressParts.length > 1 ? addressParts[addressParts.length - 2] ?? "" : "") ||
    (lead.bexMatchDetails?.street ?? lead.street ?? "");

  const displayNum = addr ?? number ?? null;
  const localityParts = [area, city].filter(Boolean);
  const streetLine = displayNum ? `${streetName}, ${displayNum}`.trim() : streetName;
  if (localityParts.length === 0 && !streetLine) return "—";
  if (localityParts.length === 0) return streetLine;
  if (!streetLine) return localityParts.join(", ");
  return `${localityParts.join(", ")}\n${streetLine}`;
}

/**
 * Address for Leads table column only: city, street, number (no area/municipality).
 */
export function getLeadDisplayAddressColumn(lead: LeadAddressSource): string {
  if (!lead) return "—";
  const city = lead.city ?? lead.bexMatchDetails?.settlement ?? null;
  const addr = lead.addr ?? lead.bexMatchDetails?.addr ?? null;

  const addressParts = (lead.address ?? "").split(", ");
  const lastPart = addressParts[addressParts.length - 1] ?? "";
  const { street, number } = parseAddressComponents(lastPart);
  const streetName =
    street ||
    (addressParts.length > 1 ? addressParts[addressParts.length - 2] ?? "" : "") ||
    (lead.bexMatchDetails?.street ?? lead.street ?? "");

  const displayNum = addr ?? number ?? null;
  const parts = [city, streetName].filter(Boolean);
  if (parts.length === 0) return "—";
  return displayNum ? `${parts.join(", ")}, ${displayNum}` : parts.join(", ");
}
